'use client';

import React, { useEffect, useState } from 'react';
import { Copy } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './button';
import { Tooltip } from './tooltip';
import { useCompositionDensity } from './composition-density';

export interface TechnicalValueProps {
  value: string;
  label: string;
  className?: string;
}

export function TechnicalValue({ value, label, className }: TechnicalValueProps) {
  const [feedback, setFeedback] = useState<'copied' | 'failed' | null>(null);
  const density = useCompositionDensity();
  useEffect(() => {
    if (feedback !== 'copied') return;
    const timer = window.setTimeout(() => setFeedback(null), 5000);
    return () => window.clearTimeout(timer);
  }, [feedback]);
  const abbreviated = value.length > 14 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value;
  return (
    <span className={cn('inline-flex min-w-0 max-w-full items-center gap-2', className)}>
      <code
        className="min-w-0 truncate font-mono text-caption text-text-body"
        aria-label={`${label}: ${abbreviated}`}
      >
        {abbreviated}
      </code>
      <Tooltip
        content={<span className="min-w-0 break-words [overflow-wrap:anywhere]">{value}</span>}
        openDelay={0}
      >
        <Button
          type="button"
          variant="tertiary"
          size="compact"
          density={density}
          className="shrink-0"
          aria-label={`${label}, copy full value`}
          onClick={async () => {
            setFeedback(null);
            try {
              await navigator.clipboard.writeText(value);
              setFeedback('copied');
            } catch {
              setFeedback('failed');
            }
          }}
        >
          <Copy aria-hidden="true" />
        </Button>
      </Tooltip>
      {feedback && (
        <span
          role={feedback === 'copied' ? 'status' : 'alert'}
          className="fixed inset-x-4 bottom-4 z-50 min-w-0 break-words rounded-overlay border border-border bg-surface-sheet p-4 font-body text-body text-text-body shadow-popover sm:left-auto sm:max-w-sm [overflow-wrap:anywhere]"
        >
          {feedback === 'copied' ? 'Copied' : 'Couldn’t copy the value. Try the copy button again.'}
        </span>
      )}
    </span>
  );
}
